import { apiFetch, isApiError } from './api';

export interface PlanLimits {
  seatLimit: number | null;
  siteLimit: number | null;
  aiReplyQuota: number | null;
  overageAmountPerReply: number;
  retrievalAmountPerCall?: number;
  toolAmountPerCall?: number;
}

export interface PlanView extends PlanLimits {
  id: string;
  code: string;
  name: string;
  period: 'monthly' | 'yearly';
  /** Smallest unit (paise for INR). */
  amount: number;
  currency: string;
  purchasable: boolean;
  /** Free days before the first charge that a checkout now would get (0 when
   *  the plan has none or the account has had its trial). */
  trialDays: number;
}

export type EntitlementState = 'unmetered' | 'trialing' | 'active' | 'past-due' | 'grace' | 'free';

export interface BillingStatus {
  enabled: boolean;
  keyId: string | null;
  entitlements: {
    state: EntitlementState;
    limits: PlanLimits | null;
    period: { start: string; end: string };
    graceUntil: string | null;
    trialEndsAt: string | null;
  };
  usage: { seats: number; sites: number; aiReplies: number };
  aiUsage: {
    billableReplies: number; modelCalls: number; failedCalls: number;
    billableRetrievals: number; billableToolCalls: number; operationCharges: Record<string, number>;
    inputTokens: number; outputTokens: number; retrievals: number; toolCalls: number;
    estimatedCostUsd: number; unknownUsageCalls: number; unpricedCalls: number;
  };
  subscription: {
    status: string;
    currentStart: string | null;
    currentEnd: string | null;
    graceUntil: string | null;
    trialEndsAt: string | null;
    cancelAtCycleEnd: boolean;
    shortUrl: string | null;
    razorpaySubscriptionId: string;
    plan: PlanView;
  } | null;
  plans: PlanView[];
  events: { id: string; event: string; receivedAt: string; processedAt: string | null }[];
}

export interface InvoiceView {
  id: string;
  status: string;
  amount: number;
  currency: string;
  url: string | null;
  issuedAt: string | null;
  paidAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

const base = '/api/billing';
const q = (accountId?: string) => (accountId ? `?accountId=${encodeURIComponent(accountId)}` : '');

export const getBilling = (accountId?: string) => apiFetch<BillingStatus>(`${base}${q(accountId)}`);

export const startCheckout = (planId: string, accountId?: string) =>
  apiFetch<{ keyId: string; subscriptionId: string; shortUrl: string | null; trialEndsAt: string | null }>(`${base}/checkout${q(accountId)}`, {
    method: 'POST',
    body: JSON.stringify({ planId }),
  });

export const verifyCheckout = (
  input: { paymentId: string; subscriptionId: string; signature: string },
  accountId?: string,
) => apiFetch<{ ok: true }>(`${base}/verify${q(accountId)}`, { method: 'POST', body: JSON.stringify(input) });

export const cancelSubscription = (atCycleEnd: boolean, accountId?: string) =>
  apiFetch<{ ok: true }>(`${base}/cancel${q(accountId)}`, { method: 'POST', body: JSON.stringify({ atCycleEnd }) });

export const changePlan = (planId: string, accountId?: string) =>
  apiFetch<{ at: 'now' | 'cycle_end' }>(`${base}/change-plan${q(accountId)}`, {
    method: 'POST',
    body: JSON.stringify({ planId }),
  });

export const listInvoices = (accountId?: string) =>
  apiFetch<{ invoices: InvoiceView[] }>(`${base}/invoices${q(accountId)}`).then((r) => r.invoices);

// ─── Platform admin ───

export interface AccountBillingRow {
  id: string;
  name: string;
  exempt: boolean;
  razorpayCustomerId: string | null;
  plan: string | null;
  status: string | null;
}

const adminBase = '/api/admin/billing';

export const listAccountBilling = () =>
  apiFetch<{ accounts: AccountBillingRow[] }>(`${adminBase}/accounts`).then((r) => r.accounts);

export const setAccountExempt = (id: string, exempt: boolean) =>
  apiFetch<{ ok: true }>(`${adminBase}/accounts/${id}`, { method: 'PATCH', body: JSON.stringify({ exempt }) });

// ─── helpers ───

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency}`;
  }
}

/** Human message for a billing API error, using the server's `message` when
 *  it sent one (Razorpay's own wording, e.g. why a plan change was refused). */
export function billingErrorMessage(err: unknown, fallback: string): string {
  if (!isApiError(err)) return fallback;
  const body = err.body as { error?: string; message?: string } | null;
  if (body?.message && body.message !== body.error) return body.message;
  switch (body?.error) {
    case 'billing-not-configured': return 'Billing is not configured on this server yet.';
    case 'already-subscribed': return 'This account already has a subscription. Change its plan instead.';
    case 'plan-not-purchasable': return 'That plan is not available to buy.';
    case 'no-active-subscription': return 'There is no active subscription to change.';
    case 'period-change-unsupported': return 'Switching between monthly and yearly needs a cancel and a new subscription.';
    case 'same-plan': return 'The account is already on that plan.';
    case 'bad-signature': return 'The payment confirmation could not be verified.';
    default: return body?.error ?? fallback;
  }
}

/** Message for a 402 plan-limit refusal from site/agent creation, or null
 *  when `err` is something else. */
export function planLimitMessage(err: unknown): string | null {
  if (!isApiError(err) || err.status !== 402) return null;
  const body = err.body as { resource?: string; limit?: number } | null;
  const what = body?.resource === 'seat' ? 'agent seats' : 'sites';
  return `This account's plan allows ${body?.limit ?? 0} ${what}. Upgrade the plan in Admin → Billing to add more.`;
}
